import { Injectable } from '@angular/core';
import { FroalaCommand } from '@r1-compass-library/models/froala/froala-command';
import { FroalaActionsService } from './froala-actions.service';
import { FroalaImagesService } from './froala-images.service';
import { FroalaImageUploadService } from './froala-image-upload.service';
import FroalaEditor from 'froala-editor';
import { FroalaEvent } from '@r1-compass-library/models/froala/froala-event';
import {
    FroalaFieldMergeDeletedCallback,
    FroalaFieldMergeDroppedCallback,
    FroalaEditorOptions,
    FroalaImageUploadHandlers,
} from '@r1-compass-library/models/froala/froala-editor-options';

@Injectable({
    providedIn: 'root',
})
export class FroalaEventsService {
    public overrideFroalaTableInsertBehavior(editor: FroalaEditor, actionsService: FroalaActionsService) {
        editor.events.on(FroalaEvent.TableInserted, function (table: HTMLTableElement) {
            const borderStyle = '1px solid';

            table.style.border = borderStyle;
            table.style.borderCollapse = 'collapse';

            table.querySelectorAll('td').forEach(cell => {
                (cell as HTMLElement).style.border = borderStyle;
            });
        });
    }

    public overrideFroalaDragDropBehavior(
        editor: FroalaEditor,
        actionsService: FroalaActionsService,
        getOnFieldMergeDropped?: () => FroalaFieldMergeDroppedCallback | undefined,
        getOnFieldMergeDeleted?: () => FroalaFieldMergeDeletedCallback | undefined
    ) {
        const onFieldMergeDeleted = (event: MouseEvent, fieldMergeElement: HTMLElement) => {
            if (editor) {
                if (editor.core.isEmpty()) {
                    editor.html.set('');
                }
                editor.undo.saveStep();
                editor.events.trigger('contentChanged', []);
            }
            getOnFieldMergeDeleted?.()?.({ event, editor, fieldMergeElement });
        };
        const dragEnter = function (event: DragEvent) {
            return actionsService.cancelEvent(event);
        };
        const dragOver = function (event: DragEvent) {
            return actionsService.cancelEvent(event);
        };
        // secondary drop event when starting outside the editor
        const dropped = function (dropEvent: DragEvent, froalaEditor: FroalaEditor = editor): boolean {
            let fieldMergeElement: HTMLElement | null = null;
            const marker: HTMLElement = froalaEditor.$el.find('.fr-marker')[0];
            if (marker && marker.parentElement) {
                const markerClassList = marker.parentElement.classList;
                const fieldMergeRawHtml = actionsService.getHtmlData(dropEvent);
                const isDragStartedWithinEditor: boolean = actionsService.isDragStartWithinEditor(dropEvent);

                if (!isDragStartedWithinEditor) {
                    const node: DocumentFragment = actionsService.createNode(fieldMergeRawHtml);
                    fieldMergeElement = node.querySelector<HTMLElement>('.field-merge');
                    actionsService.addClickEventToFieldMergeRemoveButtons(onFieldMergeDeleted, node, froalaEditor);
                    actionsService.insertNode(marker, markerClassList, node);
                }
            }
            froalaEditor.markers.remove();
            actionsService.cancelEvent(dropEvent);

            // show cursor in Firefox
            if (froalaEditor['browser'].mozilla) {
                actionsService.showCursorForFirefox(froalaEditor);
            }
            froalaEditor.undo.saveStep();
            if (fieldMergeElement) {
                getOnFieldMergeDropped?.()?.({
                    event: dropEvent,
                    editor: froalaEditor,
                    fieldMergeElement,
                });
            }
            return false;
        };
        actionsService.addEventListenersToEditorContainer(dropped, dragEnter, dragOver);
        actionsService.addClickEventToFieldMergeRemoveButtons(onFieldMergeDeleted, editor.$el[0], editor);

        let dragSource: any;
        editor.events.on(
            'dragstart',
            function (dragEvent: any) {
                dragSource = dragEvent.target;
                actionsService.setDataOnDragEvent(dragEvent);
            },
            true
        );
        // drop event inside the editor
        editor.events.on(
            'drop',
            function (dropEvent: any) {
                const isDragStartedWithinEditor: boolean = actionsService.isDragStartWithinEditor(dropEvent);
                const fieldMergeElement = isDragStartedWithinEditor ? (dragSource as HTMLElement) : undefined;
                const dropTargetClassList: DOMTokenList = dropEvent.target.classList;
                editor.markers.insertAtPoint(dropEvent.originalEvent);

                if (isDragStartedWithinEditor) {
                    const marker: HTMLElement = editor.$el.find('.fr-marker')[0];
                    if (
                        dropTargetClassList.contains('fr-view') ||
                        dropTargetClassList.contains('field-merge') ||
                        dropTargetClassList.contains('field-merge-remove')
                    ) {
                        if (
                            marker.parentElement?.classList.contains('field-merge') ||
                            marker.parentElement?.classList.contains('field-merge-remove')
                        ) {
                            marker.closest('.field-merge')?.after(dragSource);
                            actionsService.adjustSingleNodeSpacing(dragSource);
                        } else {
                            marker.replaceWith(dragSource);
                            actionsService.adjustSingleNodeSpacing(dragSource);
                        }
                    } else {
                        marker.replaceWith(dragSource);
                        actionsService.adjustSingleNodeSpacing(dragSource);
                    }
                    if (editor['browser'].mozilla) {
                        actionsService.showCursorForFirefox(editor);
                    }
                }
                // Save into undo stack the changes.
                editor.undo.saveStep();
                actionsService.cancelEvent(dropEvent);
                dragSource = '';
                if (fieldMergeElement) {
                    getOnFieldMergeDropped?.()?.({
                        event: (dropEvent.originalEvent ?? dropEvent) as DragEvent,
                        editor,
                        fieldMergeElement,
                    });
                }
                return false;
            },
            true
        );
    }

    public addFieldMergeDeleteBehavior(
        editor: FroalaEditor,
        cmd: string,
        actionsService: FroalaActionsService,
        getOnFieldMergeDeleted?: () => FroalaFieldMergeDeletedCallback | undefined
    ) {
        if (cmd === FroalaCommand.HTML || cmd === FroalaCommand.UNDO || cmd === FroalaCommand.REDO) {
            this.bindFieldMergeRemoveButtons(editor, actionsService, getOnFieldMergeDeleted);
        }
    }

    public bindFieldMergeRemoveButtons(
        editor: FroalaEditor,
        actionsService: FroalaActionsService,
        getOnFieldMergeDeleted?: () => FroalaFieldMergeDeletedCallback | undefined
    ) {
        actionsService.addClickEventToFieldMergeRemoveButtons((event, fieldMergeElement) => {
            //record snapshot in Froala undo stack
            editor.undo.saveStep();
            //notify angular-froala-wysiwyg to sync the angular form control
            editor.events.trigger('contentChanged', []);
            //notify host of deletion
            getOnFieldMergeDeleted?.()?.({ event, editor, fieldMergeElement });
        }, editor.$el[0], editor);
    }

    public addImageAdjustmentBehavior(editor: any, cmd: string) {
        if (cmd === FroalaCommand.IMAGE_SET_SIZE) {
            // Set image height/width attributes according to styling
            const imageEl: HTMLImageElement = editor.image.get()[0];
            const style = imageEl.style;
            if (
                style.width &&
                !style.width.includes('%') &&
                !isNaN(parseInt(style.width)) &&
                !isNaN(parseFloat(style.width))
            ) {
                // if a valid numeric string
                imageEl.width = parseInt(style.width);
            }
            if (
                style.height &&
                !style.height.includes('%') &&
                !isNaN(parseInt(style.height)) &&
                !isNaN(parseFloat(style.height))
            ) {
                imageEl.height = parseInt(style.height);
            }
        }
        if (cmd === FroalaCommand.IMAGE_ALIGN) {
            const imageEl: HTMLImageElement = editor.image.get()[0];
            const style = imageEl.style;
            if (style.float) {
                imageEl.align = style.float;
                style.removeProperty('float');
            }
        }
    }

    public attachImageUploadBehavior(
        froalaOptions: FroalaEditorOptions,
        clipboardService: FroalaImageUploadService,
        handlers: FroalaImageUploadHandlers
    ): void {
        clipboardService.attachImageUploadBehavior(froalaOptions, handlers);
    }

    /**
     * Attaches a capture-phase error listener to intercept broken image loads (e.g., S3 404s)
     * and display an inline warning badge without altering the underlying <img> structure.
     */
    public attachImageErrorBehavior(froalaOptions: FroalaEditorOptions, imagesService: FroalaImagesService): void {
        const existingInitialized = froalaOptions.events?.['initialized'];

        froalaOptions.events = {
            ...froalaOptions.events,
            initialized: function (this: FroalaEditor) {
                if (typeof existingInitialized === 'function') {
                    existingInitialized.call(this);
                }
                imagesService.addImageErrorCaptureListener(this.$el[0]);
            },
        };
    }
}
